import React from 'react';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, Package } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Procurement } from '@/types/database.types';

interface WarehouseInfoBadgeProps {
  procurement: Procurement;
  compact?: boolean;
}

interface WarehouseData {
  warehouse_confirmed_by?: string | null;
  warehouse_confirmation_date?: string | null;
  net_kg_payable?: number | null;
  damage_percentage?: number | null;
  sample_taken?: boolean | null;
  inspection_notes?: string | null;
}

const WarehouseInfoBadge: React.FC<WarehouseInfoBadgeProps> = ({ procurement, compact = true }) => {
  const warehouse = procurement.warehouse as WarehouseData | undefined;

  if (!warehouse) {
    return compact ? (
      <Badge variant="outline" className="bg-muted/50 text-muted-foreground border-muted text-xs">
        <Clock className="h-3 w-3 mr-1" />
        Pending
      </Badge>
    ) : (
      <span className="text-muted-foreground text-sm">No warehouse data</span>
    );
  }

  const isConfirmed = !!warehouse.warehouse_confirmed_by;
  const netPayable = warehouse.net_kg_payable || 0;
  const damagePercentage = warehouse.damage_percentage || 0;

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1.5">
              {isConfirmed ? (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {netPayable.toFixed(1)} kg
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                  <Package className="h-3 w-3 mr-1" />
                  In Progress
                </Badge>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-1 text-sm">
              <p className="font-medium">
                {isConfirmed ? 'Warehouse Confirmed' : 'Awaiting Confirmation'}
              </p>
              {isConfirmed && (
                <>
                  <p>Net Payable: {netPayable.toFixed(2)} kg</p>
                  {damagePercentage > 0 && (
                    <p>Damage: {damagePercentage.toFixed(2)}%</p>
                  )}
                  {warehouse.warehouse_confirmation_date && (
                    <p className="text-muted-foreground">
                      Confirmed: {new Date(warehouse.warehouse_confirmation_date).toLocaleDateString()}
                    </p>
                  )}
                </>
              )}
              {warehouse.inspection_notes && (
                <p className="text-muted-foreground italic">"{warehouse.inspection_notes}"</p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Expanded view
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {isConfirmed ? (
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Confirmed
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            <Package className="h-3 w-3 mr-1" />
            In Progress
          </Badge>
        )}
      </div>
      
      {isConfirmed && (
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Net Payable:</span>
            <span className="ml-2 font-medium">{netPayable.toFixed(2)} kg</span>
          </div>
          {damagePercentage > 0 && (
            <div>
              <span className="text-muted-foreground">Damage:</span>
              <span className="ml-2 font-medium">{damagePercentage.toFixed(2)}%</span>
            </div>
          )}
        </div>
      )}
      
      {warehouse.inspection_notes && (
        <p className="text-muted-foreground text-sm italic">
          Notes: {warehouse.inspection_notes}
        </p>
      )}
    </div>
  );
};

export default WarehouseInfoBadge;
